import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type ReviewConfigList as ListName,
  type ReviewConfigRow,
  useReviewConfig,
  useReviewConfigMutations,
} from "@/hooks/use-review-config";

/**
 * One review reference list — Settings → Config → Feedback.
 *
 * All four lists share this component because they share a shape. What is NOT
 * editable anywhere is the key: it is what the app branches on and what every
 * stored row holds, so the form offers it once, on create, and never again.
 */

const SCOPE_LABELS: Record<string, string> = {
  cycle: "Round",
  review: "Review",
  reviewer: "Reviewer's task",
};

export function ReviewConfigList({
  list,
  title,
  description,
  scoped = false,
}: {
  list: ListName;
  title: string;
  description: string;
  /** Statuses carry a scope; the other three lists do not. */
  scoped?: boolean;
}) {
  const { data, isLoading, isError, error, refetch } = useReviewConfig(list);
  const { update, remove, reorder } = useReviewConfigMutations(list);
  const [addOpen, setAddOpen] = useState(false);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load this list"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-medium">{title}</h2>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="size-4" aria-hidden />
          Add
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-28 w-full" />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.length === 0 && (
            <li className="p-3 text-muted-foreground text-sm">Nothing here yet.</li>
          )}
          {rows.map((row, index) => (
            <ConfigRow
              key={row.id}
              row={row}
              isFirst={index === 0}
              isLast={index === rows.length - 1}
              onMove={(direction) => reorder.mutate({ id: row.id, direction })}
              onToggleActive={(isActive) => update.mutate({ id: row.id, input: { isActive } })}
              onToggleCoversEveryone={(coversEveryone) =>
                update.mutate({ id: row.id, input: { coversEveryone } })
              }
              onRename={(input) => update.mutate({ id: row.id, input })}
              onRemove={() => remove.mutate(row.id)}
            />
          ))}
        </ul>
      )}

      <AddConfigRowDialog
        list={list}
        title={title}
        scoped={scoped}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </section>
  );
}

function ConfigRow({
  row,
  isFirst,
  isLast,
  onMove,
  onToggleActive,
  onToggleCoversEveryone,
  onRename,
  onRemove,
}: {
  row: ReviewConfigRow;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: "up" | "down") => void;
  onToggleActive: (isActive: boolean) => void;
  /** Cycle kinds only — see the control below. */
  onToggleCoversEveryone: (coversEveryone: boolean) => void;
  onRename: (input: { name: string; description: string | null }) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [description, setDescription] = useState(row.description ?? "");
  const activeId = useId();

  return (
    <li className="flex flex-wrap items-center gap-3 p-3">
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Move ${row.name} up`}
          disabled={isFirst}
          onClick={() => onMove("up")}
        >
          <ChevronUp className="size-4" aria-hidden />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Move ${row.name} down`}
          disabled={isLast}
          onClick={() => onMove("down")}
        >
          <ChevronDown className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
            <Textarea
              value={description}
              rows={2}
              placeholder="What this means (optional)"
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!name.trim()}
                onClick={() => {
                  onRename({ name: name.trim(), description: description.trim() || null });
                  setEditing(false);
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setName(row.name);
                  setDescription(row.description ?? "");
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <button type="button" className="text-left" onClick={() => setEditing(true)}>
            <span className="font-medium">{row.name}</span>
            {!row.isDeletable && (
              <StatusPill tone="neutral" label="Built in" className="ml-2 align-middle" />
            )}
            {/*
              Where a status may be used. One row can serve several stages —
              the legacy app's shape, which a migration of its rows needs.
            */}
            {row.scopes.map((scope) => (
              <StatusPill
                key={scope}
                tone="info"
                label={SCOPE_LABELS[scope] ?? scope}
                className="ml-2 align-middle"
              />
            ))}
            {row.description && (
              <span className="block text-muted-foreground text-sm">{row.description}</span>
            )}
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/*
          WI-314: what a round of this kind covers. Only cycle kinds carry it
          — `coversEveryone` is null on the other three lists — and it is a
          checkbox rather than a key the code recognises, so a renamed or
          newly-added kind cannot break generation.
        */}
        {row.coversEveryone !== null && (
          <>
            <Checkbox
              id={`${activeId}-covers`}
              checked={row.coversEveryone}
              onCheckedChange={(checked) => onToggleCoversEveryone(checked === true)}
            />
            <Label
              htmlFor={`${activeId}-covers`}
              className="mr-3 cursor-pointer font-normal text-sm"
              title="Off: the round starts empty and you name who it covers."
            >
              Covers everyone
            </Label>
          </>
        )}
        <Checkbox
          id={`${activeId}-active`}
          checked={row.isActive}
          onCheckedChange={(checked) => onToggleActive(checked === true)}
        />
        <Label htmlFor={`${activeId}-active`} className="cursor-pointer font-normal text-sm">
          Offered
        </Label>

        {/*
          A built-in row has no delete button at all — the server refuses it,
          and offering a button that always fails is worse than not offering
          one. Switching it off is the way to stop it being used.
        */}
        {row.isDeletable && (
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Remove ${row.name}`}>
                <Trash2 className="size-4" aria-hidden />
              </Button>
            }
            title={`Remove "${row.name}"?`}
            description="Anything already using it keeps it, and removal is refused while that is true. Switching it off instead stops it being offered from now on."
            confirmLabel="Remove"
            onConfirm={onRemove}
          />
        )}
      </div>
    </li>
  );
}

function AddConfigRowDialog({
  list,
  title,
  scoped,
  open,
  onOpenChange,
}: {
  list: ListName;
  title: string;
  scoped: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { create } = useReviewConfigMutations(list);
  const nameId = useId();
  const keyId = useId();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [appliesTo, setAppliesTo] = useState("review");

  const suggestedKey = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const effectiveKey = key.trim() || suggestedKey;

  const submit = async () => {
    await create.mutateAsync({
      key: effectiveKey,
      name: name.trim(),
      description: description.trim() || null,
      ...(scoped ? { appliesTo: appliesTo as "cycle" | "review" | "reviewer" } : {}),
    });
    setName("");
    setKey("");
    setDescription("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to {title.toLowerCase()}</DialogTitle>
          <DialogDescription>
            {/* Said plainly, because it is the one thing about this screen that
                surprises people. */}
            The name can be changed later; the internal code cannot. Note that the app only knows
            how to act on the ones it came with — a new one is a label you can record and report on,
            not a new step in the process.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={nameId}>Name</Label>
            <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>

          {scoped && (
            <div className="space-y-2">
              <Label>Applies to</Label>
              <Select value={appliesTo} onValueChange={setAppliesTo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cycle">A review round</SelectItem>
                  <SelectItem value="review">A person's review</SelectItem>
                  <SelectItem value="reviewer">A reviewer's task</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor={keyId}>Internal code</Label>
            <Input
              id={keyId}
              value={key}
              placeholder={suggestedKey || "lower_case_with_underscores"}
              onChange={(e) => setKey(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Filled in from the name if you leave it blank. Fixed once saved.
            </p>
          </div>

          <div className="space-y-2">
            <Label>What it means</Label>
            <Textarea
              value={description}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || !effectiveKey || create.isPending}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
